export function PrivacyPolicy() {
  return (
    <div className="page">
      <header className="page__header">
        <div className="container">
          <a href="/" className="wordmark" aria-label="MAUAI — home">
            MAUAI
          </a>
        </div>
      </header>

      <main className="page__main">
        <div className="container container--prose">
          <section className="privacy" aria-labelledby="privacy-title">
            <p className="eyebrow">PRIVACY POLICY</p>
            <h1 id="privacy-title" className="privacy__title">
              How we handle your information.
            </h1>
            <p className="privacy__effective">
              Effective <time dateTime="2026-05-25">25 May 2026</time>. Last reviewed{' '}
              <time dateTime="2026-05-25">25 May 2026</time>.
            </p>

            <p className="privacy__lede">
              MAUAI is an AI-assisted estate advisory service for foreign buyers of Mauritius
              property, available bilingually on WhatsApp and on the web. This page sets out
              the personal data we collect when you use the service, what we do with it, who
              we share it with, and the rights you have over it.
            </p>

            <hr className="rule" aria-hidden="true" />

            <p className="privacy__notice">
              MAUAI is currently in a private demonstration phase with our first advisor
              partners. Access is by invitation. This policy applies whether you reach us on
              WhatsApp, on the website, or by email.
            </p>
          </section>

          <Section number="01" title="Who we are">
            <p>
              In this policy &quot;<strong>MAUAI</strong>&quot;, &quot;<strong>we</strong>&quot;,
              and &quot;<strong>us</strong>&quot; refer to the operator of the MAUAI service,
              based in Port Louis, Mauritius. For any privacy question you can write to{' '}
              <a className="inline-link" href="mailto:privacy@mauai.mu">
                privacy@mauai.mu
              </a>{' '}
              or{' '}
              <a className="inline-link" href="mailto:hello@mauai.mu">
                hello@mauai.mu
              </a>
              .
            </p>
          </Section>

          <Section number="02" title="What we collect">
            <p>We only collect the information needed to answer your questions:</p>
            <ul className="privacy__list">
              <li>
                <strong>Your WhatsApp identifier and profile</strong> — your phone number
                (in international E.164 format) and the display name you have set on
                WhatsApp, as supplied by Meta&apos;s WhatsApp Cloud API when you message us.
              </li>
              <li>
                <strong>The content of your messages</strong> — the text, images, or
                documents you send to us, plus any reply we send back to you.
              </li>
              <li>
                <strong>Conversation metadata</strong> — timestamps, the language we
                detected, the conversation stage, and identifiers needed to thread replies
                correctly.
              </li>
              <li>
                <strong>Limited technical data</strong> — when the service handles a request,
                we log the request status, latency, and an internal turn identifier for
                diagnosing failures. Logs do not record your message content at the standard
                operating level.
              </li>
            </ul>
            <p>
              We do not place advertising cookies, analytics trackers, or third-party
              fingerprinting scripts on the website. The website serves a single page; no
              account is required to view it.
            </p>
          </Section>

          <Section number="03" title="How we use it">
            <p>We use the information above to:</p>
            <ul className="privacy__list">
              <li>
                Answer your questions about Mauritius property, residency schemes,
                regulation, and process &mdash; including by generating a reply with an AI
                model and grounding each answer in our regulatory dataset.
              </li>
              <li>
                Hand off to a regulated Mauritian advisor by name when a question warrants
                a human eye (for example, when you are ready to reserve, or when a question
                exceeds what a non-human service should answer).
              </li>
              <li>
                Operate, secure, and diagnose the service &mdash; including monitoring for
                abuse and investigating failures.
              </li>
              <li>
                Improve the service over time &mdash; we review summarised metrics
                (response latency, language coverage, escalation rate) to refine our
                regulatory dataset and conversational quality. We do not use your messages
                to train any AI model.
              </li>
            </ul>
          </Section>

          <Section number="04" title="Who we share it with">
            <p>
              MAUAI is intentionally a small dependency chain. Your information is shared
              only with the following parties, only for the purposes set out above:
            </p>
            <ul className="privacy__list">
              <li>
                <strong>Meta Platforms, Inc.</strong> &mdash; provider of the WhatsApp Cloud
                API used to receive your messages and deliver our replies. Meta&apos;s
                handling of WhatsApp message data is governed by the{' '}
                <a
                  className="inline-link"
                  href="https://www.whatsapp.com/legal/privacy-policy"
                  rel="noreferrer"
                >
                  WhatsApp Privacy Policy
                </a>
                .
              </li>
              <li>
                <strong>Anthropic, PBC</strong> &mdash; provider of the AI model that
                composes replies. The content of your message and the relevant excerpt of
                our regulatory dataset are sent to Anthropic&apos;s API for inference.
                Anthropic does not use API inputs to train its models. See the{' '}
                <a
                  className="inline-link"
                  href="https://www.anthropic.com/legal/privacy"
                  rel="noreferrer"
                >
                  Anthropic Privacy Policy
                </a>
                .
              </li>
              <li>
                <strong>The Mauritian advisor we hand off to</strong> &mdash; when you ask
                to speak to a human or when the conversation reaches the reservation stage,
                we share the conversation summary with a named regulated advisor so they
                can pick up where the AI left off.
              </li>
              <li>
                <strong>Infrastructure providers</strong> &mdash; the hosting provider for
                our servers and the DNS / TLS providers that secure the connection between
                your device and ours, strictly for the purpose of operating the service.
              </li>
            </ul>
            <p>
              We do not sell your personal information. We do not share it with advertising
              networks. We do not provide it to anyone for purposes unrelated to the
              advisory service.
            </p>
          </Section>

          <Section number="05" title="Where it is stored">
            <p>
              Conversation data is stored on managed servers maintained by MAUAI. Anthropic
              processes inference requests on infrastructure in the United States. Meta
              processes WhatsApp traffic globally. If you are in the European Economic Area,
              the United Kingdom, Switzerland, or another jurisdiction with comparable data
              protection rules, your information may be transferred to and processed in
              countries outside that jurisdiction. Where it is, we rely on the
              providers&apos; own safeguards (including Standard Contractual Clauses where
              applicable) to ensure your data is protected to an equivalent standard.
            </p>
          </Section>

          <Section number="06" title="How long we keep it">
            <p>
              We keep conversation history for as long as it is useful for the advisory
              relationship and the regulated record-keeping our advisors require, and no
              longer than is necessary. As a guide:
            </p>
            <ul className="privacy__list">
              <li>
                <strong>Conversations</strong> &mdash; retained for up to 24 months from
                the last activity, after which they are deleted or anonymised.
              </li>
              <li>
                <strong>Identity documents</strong> (passport, bank statement, proof of
                address) &mdash; retained only as long as needed to support a specific
                transaction the human advisor is working with you on, then deleted.
              </li>
              <li>
                <strong>Technical logs</strong> &mdash; rotated within 30 days.
              </li>
            </ul>
            <p>
              You can ask us to delete your data sooner at any time (see &quot;Your rights&quot;
              below). Regulated record-keeping obligations on the advisor side may require us
              to keep some records longer; we will tell you if that applies.
            </p>
          </Section>

          <Section number="07" title="Your rights">
            <p>
              Depending on where you live, you may have one or more of the following rights:
            </p>
            <ul className="privacy__list">
              <li>
                <strong>Access</strong> &mdash; a copy of the personal data we hold about
                you.
              </li>
              <li>
                <strong>Rectification</strong> &mdash; correction of data that is inaccurate
                or incomplete.
              </li>
              <li>
                <strong>Erasure</strong> &mdash; deletion of your data, subject to any
                overriding legal obligation.
              </li>
              <li>
                <strong>Restriction</strong> &mdash; pausing how we use your data while a
                concern is resolved.
              </li>
              <li>
                <strong>Portability</strong> &mdash; receipt of your data in a portable
                format.
              </li>
              <li>
                <strong>Objection</strong> &mdash; objection to processing on grounds
                related to your particular situation.
              </li>
              <li>
                <strong>Withdraw consent</strong> &mdash; where we rely on consent, you may
                withdraw it at any time.
              </li>
              <li>
                <strong>Lodge a complaint</strong> &mdash; with the Mauritius Data
                Protection Office, or with your local supervisory authority if you are in
                the EEA, UK, or Switzerland.
              </li>
            </ul>
            <p>
              To exercise any of these rights, write to{' '}
              <a className="inline-link" href="mailto:privacy@mauai.mu">
                privacy@mauai.mu
              </a>
              . We will respond within 30 days. To confirm your identity we may ask you to
              message us from the WhatsApp number associated with the conversation.
            </p>
          </Section>

          <Section number="08" title="How we protect it">
            <p>
              All traffic between your device and our servers travels over TLS. Access to
              conversation data is limited to the small number of people who need it to
              operate the service or to staff a handoff. We follow defence-in-depth
              practices: signed webhooks from Meta, scoped API credentials, server-side
              isolation of API keys, and routine review of access. No system is perfectly
              secure; we will notify you and, where required, the relevant authority if a
              personal data breach occurs.
            </p>
          </Section>

          <Section number="09" title="Children">
            <p>
              MAUAI is intended for adult property buyers. We do not knowingly direct the
              service at, or knowingly collect personal data from, children under 18. If
              you believe a child has provided us with personal data, please contact us so
              we can delete it.
            </p>
          </Section>

          <Section number="10" title="Changes to this policy">
            <p>
              We may update this policy as the service evolves or as regulation changes. The
              &quot;Effective&quot; date above always shows when the current version took
              effect. Material changes will be communicated to you on WhatsApp or by email
              before they take effect.
            </p>
          </Section>

          <Section number="11" title="Contact">
            <p>
              For any question, access request, or complaint relating to this policy, please
              write to{' '}
              <a className="inline-link" href="mailto:privacy@mauai.mu">
                privacy@mauai.mu
              </a>
              .
            </p>
          </Section>

          <hr className="rule" aria-hidden="true" />

          <p className="privacy__back-wrap">
            <a className="privacy__back" href="/">
              <span aria-hidden="true">←</span> Back to the home page
            </a>
          </p>
        </div>
      </main>

      <footer className="page__footer">
        <div className="container footer__inner">
          <p className="footer__brand">MAUAI · Port Louis, Mauritius · MMXXVI</p>
          <p className="footer__status">Currently in demo phase — by invitation.</p>
        </div>
      </footer>
    </div>
  );
}

function Section({
  number,
  title,
  children,
}: {
  number: string;
  title: string;
  children: React.ReactNode;
}) {
  const id = `s-${number}`;
  return (
    <section className="privacy__section" aria-labelledby={id}>
      <p className="privacy__section-number" aria-hidden="true">
        {number}
      </p>
      <h2 id={id} className="privacy__section-title">
        {title}
      </h2>
      <div className="privacy__section-body">{children}</div>
    </section>
  );
}
